import { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, Typography, TextField, Button, Grid, Snackbar, Alert, Chip,
} from '@mui/material'
import { Save } from '@mui/icons-material'

// Fallback demo data used when API returns empty
const DEFAULT_POLICIES = [
  { cls: 'A', label: 'Class A — High Volume', target: 60, min: 45, max: 75, color: 'error' },
  { cls: 'B', label: 'Class B — Medium Volume', target: 60, min: 45, max: 90, color: 'warning' },
  { cls: 'C', label: 'Class C — Low Volume', target: 60, min: 45, max: 120, color: 'primary' },
]

export default function DosPolicies({ settings, onSave }) {
  const [policies, setPolicies] = useState(() =>
    Array.isArray(settings?.dosPolicies) && settings.dosPolicies.length > 0
      ? settings.dosPolicies
      : DEFAULT_POLICIES,
  )
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (Array.isArray(settings?.dosPolicies) && settings.dosPolicies.length > 0) {
      setPolicies(settings.dosPolicies)
    }
  }, [settings])

  const update = (idx, field, value) => {
    setPolicies((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, [field]: Number(value) || 0 } : p)),
    )
  }

  const handleSave = async () => {
    if (onSave) {
      try {
        await onSave({ ...settings, dosPolicies: policies })
      } catch {
        // error handled by parent
      }
    }
    setSaved(true)
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Days of Supply policy defaults by ABC classification. These values drive the
        color-coded status indicators across the Planning Board and reports.
      </Typography>

      <Grid container spacing={3}>
        {policies.map((policy, idx) => (
          <Grid key={policy.cls} size={{ xs: 12, md: 4 }}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Chip label={`ABC ${policy.cls}`} color={policy.color} size="small" />
                  <Typography variant="h5">{policy.label}</Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Target DOS"
                    type="number"
                    size="small"
                    fullWidth
                    value={policy.target}
                    onChange={(e) => update(idx, 'target', e.target.value)}
                    helperText="Ideal days of supply"
                  />
                  <TextField
                    label="Minimum DOS"
                    type="number"
                    size="small"
                    fullWidth
                    value={policy.min}
                    onChange={(e) => update(idx, 'min', e.target.value)}
                    helperText="Below this triggers critical alert"
                  />
                  <TextField
                    label="Maximum DOS"
                    type="number"
                    size="small"
                    fullWidth
                    value={policy.max}
                    onChange={(e) => update(idx, 'max', e.target.value)}
                    helperText="Above this triggers overstock warning"
                  />
                </Box>

                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'surface.containerLow', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Range: {policy.min} – {policy.max} DOS &middot; Target: {policy.target} DOS
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button variant="contained" startIcon={<Save />} onClick={handleSave}>
          Save DOS Policies
        </Button>
      </Box>

      <Snackbar open={saved} autoHideDuration={3000} onClose={() => setSaved(false)}>
        <Alert severity="success" onClose={() => setSaved(false)}>DOS policies saved.</Alert>
      </Snackbar>
    </Box>
  )
}
